import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UserCircle, Loader2, Save, Camera, Trash2, Compass, BookOpen, MessageSquare } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import AvatarPicker from "@/components/AvatarPicker";
import { HONORIFICS, US_STATES, COUNTRIES } from "@/lib/brand";
import { formatUSPhone } from "@/lib/phone";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

const selCls = "mt-1.5 w-full h-11 rounded-md bg-[#1E2A5A] border border-white/10 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#E6B34A]";

export default function Profile() {
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: user?.name || "", title: user?.title || "", phone: user?.phone || "", birth_date: user?.birth_date || "",
    city: user?.city || "", state: user?.state || "", country: user?.country || "United States",
    devotion_optin: user?.devotion_optin ?? true,
    weekly_digest_optin: user?.weekly_digest_optin ?? false,
    sms_optin: user?.sms_optin ?? false,
  });
  const [saving, setSaving] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [pw, setPw] = useState({ current: "", next: "" });
  const [changingPw, setChangingPw] = useState(false);
  const avatarRef = useRef(null);
  const token = localStorage.getItem("token");
  const set = (k, v) => setForm({ ...form, [k]: v });

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/auth/profile", form);
      setUser(data.user);
      toast.success("Profile updated");
    } catch (e) { toast.error("Could not save profile"); } finally { setSaving(false); }
  };

  const onAvatar = async (file) => {
    if (!file) return;
    setUploadingAvatar(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/auth/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUser(data.user);
      toast.success("Photo updated");
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); } finally { setUploadingAvatar(false); }
  };

  const deleteAccount = async () => {
    setDeleting(true);
    try {
      await api.delete("/auth/account");
      localStorage.removeItem("token");
      setUser(false);
      toast.success("Your account has been deleted");
      navigate("/");
    } catch (e) { toast.error("Could not delete account"); setDeleting(false); }
  };

  const changePassword = async () => {
    if (pw.next.length < 6) { toast.error("New password must be at least 6 characters"); return; }
    setChangingPw(true);
    try {
      await api.post("/auth/change-password", { current_password: pw.current, new_password: pw.next });
      toast.success("Password changed");
      setPw({ current: "", next: "" });
    } catch (e) { toast.error(e.response?.data?.detail || "Could not change password"); } finally { setChangingPw(false); }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-12 py-10 max-w-2xl">
      <div className="flex items-center gap-3 mb-1">
        <UserCircle className="w-6 h-6 text-[#E6B34A]" />
        <h1 className="font-display font-black text-3xl lg:text-4xl tracking-tighter">Your Profile</h1>
      </div>
      <p className="text-zinc-400 mb-8">Update your details. Your birth date is used to reset your password.</p>

      <div className="rounded-2xl border border-white/5 bg-[#141C42] p-6 space-y-4" data-testid="profile-form">
        <AvatarPicker />

        <div>
          <label className="overline text-zinc-400">Title</label>
          <input list="honorifics2" value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Pastor, Sister, Dr…" data-testid="profile-title" className={selCls} />
          <datalist id="honorifics2">{HONORIFICS.map((h) => <option key={h} value={h} />)}</datalist>
        </div>
        <div><label className="overline text-zinc-400">Email</label><Input value={user?.email || ""} disabled className="mt-1.5 bg-[#0C1338] border-white/10 h-11 opacity-70" /></div>
        <div><label className="overline text-zinc-400">Name</label><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="profile-name" className="mt-1.5 bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A] h-11" /></div>
        <div><label className="overline text-zinc-400">Phone</label><Input type="tel" inputMode="tel" value={form.phone} onChange={(e) => set("phone", formatUSPhone(e.target.value))} placeholder="(555) 123-4567" data-testid="profile-phone" className="mt-1.5 bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A] h-11" /></div>
        <div><label className="overline text-zinc-400">Birth date</label><Input type="date" value={form.birth_date} onChange={(e) => set("birth_date", e.target.value)} data-testid="profile-birthdate" className="mt-1.5 bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A] h-11" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="overline text-zinc-400">City</label><Input value={form.city} onChange={(e) => set("city", e.target.value)} data-testid="profile-city" className="mt-1.5 bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A] h-11" /></div>
          <div>
            <label className="overline text-zinc-400">State</label>
            <select value={form.state} onChange={(e) => set("state", e.target.value)} data-testid="profile-state" className={selCls}>
              <option value="">Select…</option>
              {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>
        <div>
          <label className="overline text-zinc-400">Country</label>
          <select value={form.country} onChange={(e) => set("country", e.target.value)} data-testid="profile-country" className={selCls}>
            {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <button
          type="button"
          onClick={() => set("devotion_optin", !form.devotion_optin)}
          data-testid="profile-devotion-toggle"
          className="w-full flex items-center justify-between gap-4 rounded-xl bg-[#101735] border border-white/10 px-4 py-3 text-left hover:border-[#E6B34A]/40 transition-colors"
        >
          <div>
            <p className="font-semibold flex items-center gap-2"><BookOpen className="w-4 h-4 text-[#E6B34A]" /> Daily Devotion emails</p>
            <p className="text-xs text-zinc-400 mt-0.5">Receive an AI-written devotional in your inbox every morning at 8 AM ET.</p>
          </div>
          <span className={`relative inline-flex h-6 w-11 shrink-0 rounded-full transition-colors ${form.devotion_optin ? "bg-[#E6B34A]" : "bg-white/15"}`}>
            <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${form.devotion_optin ? "translate-x-[22px]" : "translate-x-0.5"}`} />
          </span>
        </button>
        <button
          type="button"
          onClick={() => set("weekly_digest_optin", !form.weekly_digest_optin)}
          data-testid="profile-digest-toggle"
          className="w-full flex items-center justify-between gap-4 rounded-xl bg-[#101735] border border-white/10 px-4 py-3 text-left hover:border-[#E6B34A]/40 transition-colors"
        >
          <div>
            <p className="font-semibold flex items-center gap-2"><BookOpen className="w-4 h-4 text-[#E6B34A]" /> Weekly devotion digest</p>
            <p className="text-xs text-zinc-400 mt-0.5">Get a Sunday email rounding up all of this week's devotions.</p>
          </div>
          <span className={`relative inline-flex h-6 w-11 shrink-0 rounded-full transition-colors ${form.weekly_digest_optin ? "bg-[#E6B34A]" : "bg-white/15"}`}>
            <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${form.weekly_digest_optin ? "translate-x-[22px]" : "translate-x-0.5"}`} />
          </span>
        </button>
        <button
          type="button"
          onClick={() => set("sms_optin", !form.sms_optin)}
          data-testid="profile-sms-toggle"
          className="w-full flex items-center justify-between gap-4 rounded-xl bg-[#101735] border border-white/10 px-4 py-3 text-left hover:border-[#E6B34A]/40 transition-colors"
        >
          <div>
            <p className="font-semibold flex items-center gap-2"><MessageSquare className="w-4 h-4 text-[#E6B34A]" /> Text messages (SMS)</p>
            <p className="text-xs text-zinc-400 mt-0.5">Get devotionals, show & event reminders by text. Msg &amp; data rates may apply; reply STOP anytime.</p>
          </div>
          <span className={`relative inline-flex h-6 w-11 shrink-0 rounded-full transition-colors ${form.sms_optin ? "bg-[#E6B34A]" : "bg-white/15"}`}>
            <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${form.sms_optin ? "translate-x-[22px]" : "translate-x-0.5"}`} />
          </span>
        </button>
        <button onClick={save} disabled={saving} data-testid="profile-save" className="w-full h-12 rounded-xl bg-[#E6B34A] text-[#0A1030] font-bold hover:bg-[#CF9A34] transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
          {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Save className="w-4 h-4" /> Save changes</>}
        </button>
      </div>

      <div className="mt-6 rounded-2xl border border-white/5 bg-[#141C42] p-6 flex items-center justify-between gap-4" data-testid="replay-tutorial-card">
        <div>
          <h3 className="font-display font-bold text-lg flex items-center gap-2"><Compass className="w-4 h-4 text-[#E6B34A]" /> App tour</h3>
          <p className="text-sm text-zinc-400 mt-1">New here or need a refresher? Replay the guided walkthrough.</p>
        </div>
        <button onClick={() => window.dispatchEvent(new Event("start-tour"))} data-testid="replay-tutorial" className="shrink-0 px-5 py-2.5 rounded-xl bg-[#E6B34A]/10 text-[#E6B34A] font-bold hover:bg-[#E6B34A]/20 transition-colors">
          Replay tutorial
        </button>
      </div>

      <div className="mt-6 rounded-2xl border border-white/5 bg-[#141C42] p-6" data-testid="change-password">
        <h3 className="font-display font-bold text-lg mb-4">Change password</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><label className="overline text-zinc-400">Current password</label><Input type="password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} data-testid="cp-current" className="mt-1.5 bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A] h-11" /></div>
          <div><label className="overline text-zinc-400">New password</label><Input type="password" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} data-testid="cp-new" className="mt-1.5 bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A] h-11" /></div>
        </div>
        <button onClick={changePassword} disabled={changingPw || !pw.current || !pw.next} data-testid="cp-submit" className="mt-4 px-5 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 font-semibold transition-colors disabled:opacity-50">
          {changingPw ? "Updating…" : "Update password"}
        </button>
      </div>

      <div className="mt-6 rounded-2xl border border-[#FF3B30]/20 bg-[#FF3B30]/5 p-6">
        <h3 className="font-display font-bold text-lg text-[#FF3B30]">Danger zone</h3>
        <p className="text-sm text-zinc-400 mt-1 mb-4">Permanently delete your account, favorites and requests. This can't be undone.</p>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <button data-testid="delete-account" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#FF3B30]/10 text-[#FF3B30] hover:bg-[#FF3B30]/20 font-semibold transition-colors">
              <Trash2 className="w-4 h-4" /> Delete my account
            </button>
          </AlertDialogTrigger>
          <AlertDialogContent className="bg-[#141C42] border-white/10 text-white" data-testid="delete-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle className="font-display">Are you sure?</AlertDialogTitle>
              <AlertDialogDescription className="text-zinc-400">This will permanently delete your account and all your data. This action cannot be undone.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="delete-cancel" className="bg-white/5 border-white/10 text-white hover:bg-white/10">Cancel</AlertDialogCancel>
              <AlertDialogAction data-testid="delete-confirm" onClick={deleteAccount} disabled={deleting} className="bg-[#FF3B30] text-white hover:bg-[#e0342a]">
                {deleting ? "Deleting…" : "Yes, delete my account"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}

